import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Card, CardBody } from '@/components/ui/Card'
import { adminReportService, UsageReport, RelationshipReport } from '@/services/adminReportService'
import { useToast } from '@/contexts/ToastContext'

interface StatTileProps {
  label: string
  value: number | null
}

const StatTile: React.FC<StatTileProps> = ({ label, value }) => (
  <Card>
    <CardBody>
      <div className="text-sm text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-gray-900">
        {value === null ? <span className="text-gray-400">Unavailable</span> : value.toLocaleString()}
      </div>
    </CardBody>
  </Card>
)

function formatDuration(ms: number | null): string {
  if (ms === null) return 'Unavailable'
  const days = ms / (1000 * 60 * 60 * 24)
  return `${days.toFixed(1)} days`
}

function reportTimestamp(usage: UsageReport | null, relationship: RelationshipReport | null): string | null {
  return usage?.generatedAt ?? relationship?.generatedAt ?? null
}

const AdminReportsPage: React.FC = () => {
  const { showToast } = useToast()

  const [usageReport, setUsageReport] = useState<UsageReport | null>(null)
  const [relationshipReport, setRelationshipReport] = useState<RelationshipReport | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchingRef = useRef(false)

  const fetchReports = useCallback(async () => {
    if (fetchingRef.current) return

    try {
      fetchingRef.current = true
      setLoading(true)
      const [usage, relationships] = await Promise.all([
        adminReportService.getUsageReport(),
        adminReportService.getRelationshipReport()
      ])
      setUsageReport(usage)
      setRelationshipReport(relationships)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to fetch reports'
      })
    } finally {
      setLoading(false)
      fetchingRef.current = false
    }
  }, [showToast])

  useEffect(() => {
    fetchReports()
  }, [fetchReports])

  const unavailableServices = Array.from(
    new Set([...(usageReport?.unavailableServices ?? []), ...(relationshipReport?.unavailableServices ?? [])])
  )
  const isPartial = Boolean(usageReport?.partial || relationshipReport?.partial)
  const generatedAt = reportTimestamp(usageReport, relationshipReport)
  const byStatus = relationshipReport?.byStatus

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900">Reports</h2>
      <p className="mt-2 text-gray-600">Platform usage and relationship outcomes at a glance.</p>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      ) : !usageReport && !relationshipReport ? (
        <p className="mt-6 text-center text-gray-500">No report data available.</p>
      ) : (
        <>
          {isPartial && (
            <div className="mt-6 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              Some services were unreachable when these reports were generated
              ({unavailableServices.join(', ')}). Showing the data that is available.
            </div>
          )}

          {generatedAt && (
            <p className="mt-4 text-xs text-gray-400">
              Generated {new Date(generatedAt).toLocaleString()}
              {usageReport?.cached || relationshipReport?.cached ? ' (cached)' : ''}
            </p>
          )}

          <h3 className="mt-6 text-lg font-semibold text-gray-900">Users</h3>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile label="Total users" value={usageReport?.users?.totalUsers ?? null} />
            <StatTile label="Mentors" value={usageReport?.users?.usersByRole.mentors ?? null} />
            <StatTile label="Mentees" value={usageReport?.users?.usersByRole.mentees ?? null} />
            <StatTile label="Admins" value={usageReport?.users?.usersByRole.admins ?? null} />
          </div>

          <h3 className="mt-6 text-lg font-semibold text-gray-900">Relationships</h3>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile label="Total relationships" value={usageReport?.relationships?.total ?? null} />
            <StatTile label="Active" value={usageReport?.relationships?.active ?? null} />
            <StatTile label="Ended" value={usageReport?.relationships?.ended ?? null} />
          </div>

          <h3 className="mt-6 text-lg font-semibold text-gray-900">Relationship report</h3>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile label="Pending" value={byStatus?.pending ?? null} />
            <StatTile label="Active" value={byStatus?.active ?? null} />
            <StatTile label="Completed" value={byStatus?.completed ?? null} />
            <StatTile label="Cancelled" value={byStatus?.cancelled ?? null} />
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardBody>
                <div className="text-sm text-gray-500">Average relationship duration</div>
                <div className="mt-1 text-2xl font-bold text-gray-900">
                  {formatDuration(relationshipReport?.averageDurationMs ?? null)}
                </div>
              </CardBody>
            </Card>
          </div>

          <h3 className="mt-6 text-lg font-semibold text-gray-900">Activity</h3>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile label="Tasks" value={usageReport?.tasks?.total ?? null} />
            <StatTile label="Messages" value={usageReport?.messages?.total ?? null} />
            <StatTile label="Meetings" value={usageReport?.meetings?.total ?? null} />
            <StatTile label="Resources" value={usageReport?.resources?.total ?? null} />
          </div>
        </>
      )}
    </div>
  )
}

export default AdminReportsPage
